import * as React from 'react';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { StaticImage } from 'gatsby-plugin-image';
import { EditorContext } from '@/features/editor/context/editor-context';
import { HSpace } from '@/components/spacing';
import { TargetingDeviceType } from '@/webapi/use-experience-api';
import { DeviceType } from '@/utils/definitions';
import { SharedElementOverlay } from '@/components/shared-element-overlay';
import { NavigationOverlay } from '@/features/editor/preview-navigation/navigation-overlay';

export const DEVICE_NAV_ID = `vsly-device-navigation`;

export function PreviewNavigation() {
  const {
    deviceNavigation: { urlToStorePage, previewUrl },
    devicePreview: {
      setPreviewDevice,
      editorState: { device: previewDevice },
    },
    experienceState: {
      currentExperience: { targeting },
    },
  } = useContext(EditorContext);

  const [isOverlayVisible, setIsOverlayVisible] = useState(false);
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const currentPage = useMemo(() => urlToStorePage(previewUrl), [previewUrl]);

  const showMobile = useMemo(
    () =>
      [
        TargetingDeviceType.MOBILE,
        TargetingDeviceType.ALL_DEVICES,
        undefined,
      ].includes(targeting.device),
    [targeting?.device],
  );

  const showDesktop = useMemo(
    () =>
      [
        TargetingDeviceType.DESKTOP,
        TargetingDeviceType.ALL_DEVICES,
        undefined,
      ].includes(targeting.device),
    [targeting?.device],
  );

  useEffect(() => {
    if (
      previewDevice === DeviceType.Desktop &&
      targeting?.device === TargetingDeviceType.MOBILE
    ) {
      setPreviewDevice(DeviceType.Mobile);
    }

    if (
      previewDevice === DeviceType.Mobile &&
      targeting?.device === TargetingDeviceType.DESKTOP
    ) {
      setPreviewDevice(DeviceType.Desktop);
    }
  }, [previewDevice, targeting?.device]);

  const switcherWrapperLeft = useMemo(() => {
    if (
      !targeting?.device ||
      targeting?.device === TargetingDeviceType.ALL_DEVICES
    ) {
      return `-3.8rem`;
    }
    return `0`;
  }, [targeting?.device]);

  const selectedDeviceLeft = useMemo(() => {
    if (
      !targeting?.device ||
      targeting?.device === TargetingDeviceType.ALL_DEVICES
    ) {
      if (previewDevice === DeviceType.Mobile) return `50%`;
      return `0`;
    }
    return `0`;
  }, [targeting?.device, previewDevice]);

  return (
    <Wrapper id={DEVICE_NAV_ID} ref={fromRef}>
      <RelativePopup ref={toRef} />
      <SharedElementOverlay
        isVisible={isOverlayVisible}
        showBackdrop
        onBackdropClick={() => setIsOverlayVisible(false)}
        fromElement={fromRef}
        toPosition={toRef}
        widthMultiplier={1.2}
        extraFrom={{
          backgroundColor: `rgba(255,255,255,0)`,
          boxShadow: `0 12px 0 84px rgba(0,0,0,0)`,
          borderRadius: `2rem`,
          opacity: 0.8,
        }}
        extraTo={{
          backgroundColor: `rgba(255,255,255,1)`,
          boxShadow: `0 12px 0 84px rgba(0,0,0,0.06)`,
          borderRadius: `1rem`,
          opacity: 1,
        }}
      >
        <NavigationOverlay close={() => setIsOverlayVisible(false)} />
      </SharedElementOverlay>
      <SwitcherWrapper
        style={{
          left: switcherWrapperLeft,
        }}
      >
        <SelectedIcon style={{ left: selectedDeviceLeft }} />
        {showDesktop && (
          <DesktopIcon
            selected={previewDevice === DeviceType.Desktop}
            onClick={() => {
              setPreviewDevice(DeviceType.Desktop);
            }}
          />
        )}
        {showMobile && (
          <MobileIcon
            selected={previewDevice === DeviceType.Mobile}
            onClick={() => {
              setPreviewDevice(DeviceType.Mobile);
            }}
          />
        )}
      </SwitcherWrapper>
      <span
        onClick={() => {
          setIsOverlayVisible(true);
        }}
      >
        View Page: {currentPage.kind.toString()}
      </span>
      <HSpace value={1.8} />
      <DownArrow />
    </Wrapper>
  );
}

const Wrapper = styled.div`
  position: relative;

  background: #e7ecf2;
  padding: 1rem 1.8rem 1rem 5rem;
  border-radius: 2rem;
  margin: -1.5rem 0 1.5rem 0;

  color: #767e8e;
  font-size: 1.2rem;
  font-weight: 600;
  font-family: 'JetBrains Mono', serif;
  text-transform: uppercase;

  display: flex;
  flex-direction: row;
  justify-content: center;

  transition: opacity 0.3s ease-in-out;

  span {
    cursor: pointer;
  }
`;

const RelativePopup = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: -65%;
  height: 50rem;
  width: 200%;

  opacity: 0;
`;

const DownArrow = () => (
  <StaticImage
    src="../../../assets/down_arrow.svg"
    alt="down"
    height={5}
    objectFit="contain"
    placeholder="none"
    loading="eager"
  />
);

const SwitcherWrapper = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  height: 100%;
  border-radius: 2rem;
  background: #d0dbe7;
`;

const SelectedIcon = styled.span`
  position: absolute;
  background: white;
  border-radius: 50%;
  height: 100%;
  width: 3.7rem;
  box-shadow: 0 40px 57px 0 rgba(177, 217, 203, 0.18),
    0 2px 15px 0 rgba(0, 0, 0, 0.05);
`;

const Icon = styled.div`
  z-index: 1;
  cursor: pointer;
  height: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem 0.9rem;
`;

const DesktopIcon = ({ onClick, selected }) => (
  <Icon onClick={onClick}>
    <svg width="20px" height="20px" viewBox="0 0 46 45">
      <g
        id="Page-1"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="130---CREATOS---womens---21----ACTIONS"
          transform="translate(-2697.000000, -281.000000)"
        >
          <g id="Group-10" transform="translate(2697.310811, 281.562500)">
            <rect
              id="Rectangle-Copy"
              fill={selected ? `rgb(14,133,255)` : `rgb(179,191,207)`}
              x="11.2818533"
              y="41.25"
              width="23.503861"
              height="2.8125"
              rx="1.40625"
            />
            <path
              d="M17.8800338,34.6835183 L28.1875338,34.6835183 C28.7066814,34.6835183 29.1275338,35.1043707 29.1275338,35.6235183 L29.1275338,36.5639817 C29.1275338,37.0831293 28.7066814,37.5039817 28.1875338,37.5039817 L17.8800338,37.5039817 C17.3608861,37.5039817 16.9400338,37.0831293 16.9400338,36.5639817 L16.9400338,35.6235183 C16.9400338,35.1043707 17.3608861,34.6835183 17.8800338,34.6835183 Z"
              id="Rectangle-Copy-5"
              fill={selected ? `rgb(14,133,255)` : `rgb(179,191,207)`}
              transform="translate(23.033784, 36.093750) rotate(-270.000000) translate(-23.033784, -36.093750) "
            />
            <rect
              id="Rectangle"
              stroke={selected ? `rgb(14,133,255)` : `rgb(179,191,207)`}
              strokeWidth="2.82"
              x="1.41"
              y="1.41"
              width="42.3074131"
              height="29.055"
              rx="5.64"
            />
          </g>
        </g>
      </g>
    </svg>
  </Icon>
);

const MobileIcon = ({ onClick, selected }) => (
  <Icon onClick={onClick}>
    <svg width="20px" height="20px" viewBox="0 0 31 44">
      <g
        id="Page-1"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
      >
        <g
          id="130---CREATOS---womens---21----ACTIONS"
          transform="translate(-2800.000000, -281.000000)"
        >
          <g id="Group-14" transform="translate(2800.727799, 281.562500)">
            <rect
              id="Rectangle"
              stroke={selected ? `rgb(14,133,255)` : `rgb(179,191,207)`}
              strokeWidth="2.82"
              x="1.41"
              y="1.41"
              width="27.2649421"
              height="39.7954891"
              rx="5.64"
            />
            <rect
              id="Rectangle"
              fill={selected ? `rgb(14,133,255)` : `rgb(179,191,207)`}
              x="11.2818533"
              y="31.875"
              width="8.46138996"
              height="2.8125"
              rx="1.40625"
            />
          </g>
        </g>
      </g>
    </svg>
  </Icon>
);
